import { useMemo } from 'react'
import { useWatch } from 'react-hook-form'

import { TRADES_FILTERS_FIELDS } from '../../components/trades-filters'
import { useFilteredGetByTenantAccountQuery } from '../useFilteredNotes'

export const useFilteredAccounts = () => {
  const { data, isLoading } = useFilteredGetByTenantAccountQuery()

  const searchValue = useWatch({ name: 'trades-search' })
  const portfolioSearchValue = useWatch({ name: 'portfolio-search' })

  const [
    tradesActive,
    tradesClosing,
    tradesClosed,
    tradesSuspended,
    tradesModel,
    tradesSelfSelect,
    tradesFundOfFunds,
    tradesDeltaOne,
    tradesConstantProportionLeverage,
    tradesRiskManaged,
    tradesVolatilityControlLeverage,
    tradesContinuousProtection,
    tradesTermProtection
  ] = useWatch({ name: TRADES_FILTERS_FIELDS })

  return useMemo(() => {
    if (isLoading || !data) return []

    const accounts = data.map(account => ({
      ...account,
      strategyTemplateName:
        account.accountSource === 'Dominion'
          ? 'Continuous Protection'
          : account.strategyTemplateName
    }))

    const areAllFiltersInactive = [
      searchValue,
      portfolioSearchValue,
      tradesActive,
      tradesClosing,
      tradesClosed,
      tradesSuspended,
      tradesModel,
      tradesSelfSelect,
      tradesFundOfFunds,
      tradesDeltaOne,
      tradesConstantProportionLeverage,
      tradesRiskManaged,
      tradesVolatilityControlLeverage,
      tradesContinuousProtection,
      tradesTermProtection
    ].every(value => Boolean(value) === false)

    const areAnyStrategiesActive =
      tradesDeltaOne ||
      tradesConstantProportionLeverage ||
      tradesRiskManaged ||
      tradesVolatilityControlLeverage ||
      tradesContinuousProtection ||
      tradesTermProtection

    const strategiesValues = {
      'Delta One': tradesDeltaOne,
      'Constant Proportion Leverage': tradesConstantProportionLeverage,
      'Risk Managed': tradesRiskManaged,
      'Volatility Control Leverage': tradesVolatilityControlLeverage,
      'Continuous Protection': tradesContinuousProtection,
      'Term Protection': tradesTermProtection
    }

    const activeStrategies = Object.entries(strategiesValues)
      .filter(([, value]) => value)
      .map(([key]) => key)

    return accounts.filter(account => {
      if (areAllFiltersInactive) return true

      const passesSearch = searchValue
        ? account.accountId.toLowerCase().includes(searchValue.toLowerCase()) ||
          account.accountName.toLowerCase().includes(searchValue.toLowerCase())
        : true

      const passesPortfolioSearch = portfolioSearchValue
        ? account.portfolioId.toLowerCase().includes(portfolioSearchValue.toLowerCase()) ||
          account.portfolioName.toLowerCase().includes(portfolioSearchValue.toLowerCase())
        : true

      const areAnyStatusActive = tradesActive || tradesClosing || tradesClosed || tradesSuspended
      const statusValues = {
        New: tradesActive,
        Running: tradesActive,
        Closing: tradesClosing,
        Closed: tradesClosed,
        Suspended: tradesSuspended
      }

      const passesStatus = areAnyStatusActive
        ? Object.entries(statusValues)
            .filter(([, value]) => value)
            .map(([key]) => key)
            .includes(account.status)
        : true

      const passesModel =
        tradesModel && !tradesSelfSelect && !tradesFundOfFunds
          ? account.portfolioType === 'ModelPortfolio'
          : true

      const passesSelfSelect =
        tradesSelfSelect && !tradesModel && !tradesFundOfFunds
          ? account.portfolioType === 'SelfSelect'
          : true

      const passesFundOfFunds =
        tradesFundOfFunds && !tradesModel && !tradesSelfSelect
          ? account.portfolioType === 'FundOfFunds'
          : true

      const passesStrategies = areAnyStrategiesActive
        ? activeStrategies.includes(account.strategyTemplateName)
        : true

      return (
        passesSearch &&
        passesPortfolioSearch &&
        passesStatus &&
        passesModel &&
        passesSelfSelect &&
        passesFundOfFunds &&
        passesStrategies
      )
    })
  }, [
    isLoading,
    data,
    searchValue,
    portfolioSearchValue,
    tradesActive,
    tradesClosing,
    tradesClosed,
    tradesSuspended,
    tradesModel,
    tradesSelfSelect,
    tradesFundOfFunds,
    tradesDeltaOne,
    tradesConstantProportionLeverage,
    tradesRiskManaged,
    tradesVolatilityControlLeverage,
    tradesContinuousProtection,
    tradesTermProtection
  ])
}
